import { useState } from 'react'
import { Tab, TabPanel, Tabs, TabsList } from '@/components/Tabs'
import type { OperatingCostKind } from '@/types/api'
import { useOperatingCosts } from './hooks/useOperatingCosts'
import { CreateCategoryModal } from './components/CreateCategoryModal'
import { LegalPrimeProfitPanel } from './components/LegalPrimeProfitPanel'
import { OperatingCostKindPanel } from './components/OperatingCostKindPanel'
import { OperatingCostTotalsTable } from './components/OperatingCostTotalsTable'
import styles from './CustosOperacionaisPage.module.css'

export function CustosOperacionaisPage() {
  const costs = useOperatingCosts()
  const [isCreateOpen, setIsCreateOpen] = useState(false)
  const [createKind, setCreateKind] = useState<OperatingCostKind>('fixed')
  const categories = costs.snapshot?.categories ?? []

  function openCreateModal(kind: OperatingCostKind) {
    setCreateKind(kind)
    setIsCreateOpen(true)
  }

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <div>
          <h1 className={styles.title}>Custos operacionais</h1>
          <p className={styles.subtitle}>
            Cadastre os custos da operação com nomes livres. Nos custos fixos, escolha o
            mês inicial e o mês final para repetir o valor em cada mês. Cada lançamento
            pode ser editado, duplicado ou excluído separadamente. A API WhatsApp lança
            automaticamente a tarifa configurada a cada venda paga. O split de comissão de
            terceiros usa o percentual padrão ou o personalizado da campanha. O split
            LEGAL PRIME e nomes antigos como Fábio entram como receita da plataforma. O
            servidor AWS usa lançamentos por mês e ano. O % PIX e o % Crédito guardam taxa
            padrão e valores personalizados por campanha. Marketing e aquisição têm
            lançamentos mensais que alimentam o CAC. Nos demais custos em reais, informe a
            competência para incluí-los no gráfico mensal. Competências e horários seguem
            o calendário de Brasília.
          </p>
        </div>
      </header>

      {costs.error && !isCreateOpen ? (
        <p className={styles.bannerError} role="alert">
          {costs.error}
        </p>
      ) : null}
      {costs.success ? (
        <p className={styles.bannerSuccess} role="status">
          {costs.success}
        </p>
      ) : null}

      {costs.isLoading && costs.snapshot === null ? (
        <p className={styles.empty}>Carregando custos...</p>
      ) : costs.snapshot === null ? (
        <p className={styles.empty}>Nenhum custo para exibir.</p>
      ) : (
        <>
          <Tabs defaultTab="dashboard" id="operating-cost-kinds">
            <TabsList aria-label="Áreas de custos operacionais">
              <Tab value="dashboard">Dashboard</Tab>
              <Tab value="fixed">Fixos</Tab>
              <Tab value="variable">Variáveis</Tab>
              <Tab value="profit">Receita</Tab>
            </TabsList>

            <TabPanel value="dashboard">
              <OperatingCostTotalsTable
                categories={costs.snapshot.categories}
                totals={costs.snapshot.totals}
              />
            </TabPanel>

            <TabPanel value="fixed">
              <OperatingCostKindPanel
                kind="fixed"
                categories={categories}
                isSaving={costs.isSaving}
                onCreateCategory={() => openCreateModal('fixed')}
                onRename={costs.renameCategory}
                onDeleteCategory={costs.deleteCategory}
                onCreateEntry={costs.createEntry}
                onUpdateEntry={costs.updateEntry}
                onDeleteEntry={costs.deleteEntry}
                onCreateMonthlyEntry={costs.createMonthlyEntry}
                onUpdateMonthlyEntry={costs.updateMonthlyEntry}
                onDeleteMonthlyEntry={costs.deleteMonthlyEntry}
                onUpdateUnitRate={costs.updateUnitRate}
                onFeesChanged={costs.reload}
              />
            </TabPanel>

            <TabPanel value="variable">
              <OperatingCostKindPanel
                kind="variable"
                categories={categories}
                isSaving={costs.isSaving}
                onCreateCategory={() => openCreateModal('variable')}
                onRename={costs.renameCategory}
                onDeleteCategory={costs.deleteCategory}
                onCreateEntry={costs.createEntry}
                onUpdateEntry={costs.updateEntry}
                onDeleteEntry={costs.deleteEntry}
                onCreateMonthlyEntry={costs.createMonthlyEntry}
                onUpdateMonthlyEntry={costs.updateMonthlyEntry}
                onDeleteMonthlyEntry={costs.deleteMonthlyEntry}
                onUpdateUnitRate={costs.updateUnitRate}
                onFeesChanged={costs.reload}
              />
            </TabPanel>

            <TabPanel value="profit">
              <LegalPrimeProfitPanel />
            </TabPanel>
          </Tabs>
        </>
      )}

      <CreateCategoryModal
        isOpen={isCreateOpen}
        isSaving={costs.isSaving}
        kind={createKind}
        error={isCreateOpen ? costs.error : null}
        onClose={() => setIsCreateOpen(false)}
        onSubmit={costs.createCategory}
      />
    </div>
  )
}
